<template>
  <div class="items-income-report">
    <div class="filter-card">
      <!-- Row 1: date + scope chips + primary action -->
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field field-scope">
          <label class="field-lbl">ประเภท</label>
          <div class="chip-group">
            <label class="chip" :class="{ 'chip-active': itemsType.includes(1) }">
              <input type="checkbox" :value="1" v-model="itemsType" />
              <span>รักษาทั่วไป</span>
            </label>
            <label class="chip" :class="{ 'chip-active': itemsType.includes(2) }">
              <input type="checkbox" :value="2" v-model="itemsType" />
              <span>แพ็กเกจจัดฟัน</span>
            </label>
            <label class="chip" :class="{ 'chip-active': itemsType.includes(3) }">
              <input type="checkbox" :value="3" v-model="itemsType" />
              <span>ยา และอุปกรณ์</span>
            </label>
          </div>
        </div>

        <b-button
          variant="primary"
          class="btn-primary-lg"
          data-testid="report-search-button"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>

      <!-- Row 2: search filters + saved filters -->
      <div class="fc-row fc-row-secondary">
        <div class="field">
          <label class="field-lbl">รายการรักษา</label>
          <b-input-group>
            <multiselect
              class="col p-0 multiselect-h-36px"
              :style="{ minHeight: '36px' }"
              v-model="selectedTx"
              :options="getGroupTxListNoFav"
              :limit="5"
              :multiple="true"
              :close-on-select="false"
              group-values="libs"
              group-label="category"
              :group-select="true"
              placeholder="ค้นหาการรักษาที่ต้องการกรอง"
              :custom-label="customTxDrugsLabel"
              track-by="nameTh"
              selectLabel
              selectedLabel
              deselectLabel
            >
              <template #option="props">
                <span v-if="props.option.$isLabel">
                  {{ props.option.$groupLabel }}
                </span>
              </template>
              <template #clear="props">
                <div
                  v-if="selectedTx.length"
                  class="multiselect__clear"
                  @mousedown.prevent.stop="clearTxFilter(props)"
                ></div>
              </template>
              <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
            </multiselect>
            <b-input-group-append>
              <b-button
                size="sm"
                class="fi-fav"
                @click="$refs.nameInputModal.openModal()"
              >
                <i class="far fa-star"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <div class="field">
          <label class="field-lbl">ยาและสินค้า</label>
          <b-input-group>
            <multiselect
              class="col p-0 multiselect-h-36px"
              :style="{ minHeight: '36px' }"
              v-model="selectedDrug"
              :options="getGroupedItemListNoFav"
              :limit="5"
              :multiple="true"
              :close-on-select="false"
              group-values="libs"
              group-label="category"
              :group-select="true"
              placeholder="ค้นหาการยาและสินค้าที่ต้องการกรอง"
              :custom-label="customTxDrugsLabel"
              track-by="nameTh"
              selectLabel
              selectedLabel
              deselectLabel
            >
              <template #option="props">
                <span v-if="props.option.$isLabel">
                  {{ props.option.$groupLabel }}
                </span>
              </template>
              <template #clear="props">
                <div
                  v-if="selectedDrug.length"
                  class="multiselect__clear"
                  @mousedown.prevent.stop="clearDrugFilter(props)"
                ></div>
              </template>
              <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
            </multiselect>
            <b-input-group-append>
              <b-button
                size="sm"
                class="fi-fav"
                @click="$refs.nameInputModal.openModal()"
              >
                <i class="far fa-star"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <div class="field">
          <label class="field-lbl">ตัวกรองที่บันทึก</label>
          <FavFilter
            ref="favFilter"
            storageName="favouriteFilter"
            :height="36"
            @listClicked="applySelectedTx"
          />
        </div>
      </div>
    </div>

    <!-- {{favTxValue}} -->
    <!-- {{selectedTx}} -->
    <div
      id="report-elements"
      ref="reportElements"
      :class="{ 'my-fullscreen': isFullscreen }"
    >
      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <div class="seg" role="tablist" aria-label="มุมมอง">
            <span class="seg-lbl">มุมมอง:</span>
            <button
              type="button"
              class="seg-btn"
              :class="{ active: viewMode === 'financial' }"
              data-testid="view-mode-financial"
              @click="viewMode = 'financial'"
            >
              💰 รายได้ &amp; DF/CF
            </button>
            <button
              type="button"
              class="seg-btn"
              :class="{ active: viewMode === 'payment' }"
              data-testid="view-mode-payment"
              @click="viewMode = 'payment'"
            >
              💳 วิธีชำระ
            </button>
          </div>

          <b-button
            size="sm"
            variant="outline-secondary"
            @click="isFullscreen = !isFullscreen"
          >
            <i class="fas fa-expand"></i>
          </b-button>
          <b-button
            size="sm"
            variant="outline-success"
            @click="excelExport('branchItemsIncomeTable')"
          >
            <i class="fas fa-file-excel"></i>
          </b-button>
        </div>
      </div>

      <b-table
        id="branchItemsIncomeTable"
        ref="branchItemsIncomeTable"
        class="my-0 sticky-footer items-income-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="fields"
        sort-by="creationDt"
        :sort-desc="true"
        v-model="visibleRows"
        :responsive="true"
        caption="Double click เพื่อดูใบเสร็จ/ DF และ CF คำนวนจากการรับชำระของคนไข้ในแต่ละครั้ง"
        :sticky-header="tableHeight"
        :per-page="perPage"
        :current-page="currentPage"
        show-empty
        @row-dblclicked="openReceipt"
      >
        <template #thead-top>
          <b-tr class="grp-band-row">
            <b-th colspan="7" class="grp-pat grp-band">
              ข้อมูลผู้ป่วย / การรักษา
            </b-th>
            <b-th colspan="5" class="grp-rev grp-band">รายได้</b-th>
            <b-th
              v-if="showFinancial"
              :colspan="feeColspan"
              class="grp-fee grp-band"
            >
              ส่วนแบ่ง{{ hasAnyFee ? " &amp; ค่าธรรมเนียม" : "" }}
            </b-th>
            <b-th v-if="showPayment" class="grp-pay grp-band">
              วิธีชำระเงินต่อรายการ
            </b-th>
          </b-tr>
        </template>

        <template #head(processingFeeBaht)="data">
          <processing-fee-tooltip
            :label="data.label"
            popover-id="processingFeePopover"
          />
        </template>
        <template #cell(index)="row">{{ row.item.row }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(treatType)="row">{{ formatType(row.value) }}</template>
        <template #cell(paymentChips)="row">
          <PaymentChips :pmts="row.item.paymentAllocations || []" />
        </template>

        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td class="text-left" colspan="10">
              รวม {{ filteredList.length }} รายการ
            </td>
            <td>{{ sum("paid") }}</td>
            <td>—</td>

            <template v-if="showFinancial">
              <td v-if="hasAnyFee">{{ sum("processingFeeBaht") }}</td>
              <td>{{ sum("paidDf") }}</td>
              <td>{{ sum("paidCf") }}</td>
            </template>

            <td v-if="showPayment" class="text-left">
              <PaymentChips :pmts="totalsByMethod" />
            </td>
          </b-tr>
        </template>
        <template #empty>
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>
    <NameInput ref="nameInputModal" @submitName="addFav" />
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";
import ProcessingFeeTooltip from "@/components/tooltip/ProcessingFee";

// import PdfExport from "@/components/PdfExport";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

import XLSX from "xlsx";

import FavFilter from "@/components/FavFilter";
import NameInput from "@/components/modal/NameInput";
import PaymentChips from "@/components/reports/PaymentChips";

export default {
  name: "BranchItemsIncomeReport",
  components: {
    Dialog,
    Loading,
    FavFilter,
    NameInput,
    ProcessingFeeTooltip,
    PaymentChips,
  },
  data() {
    return {
      isLoading: false,
      viewMode: "financial", // "financial" | "payment"

      dateRange: [],

      //table
      tableBusy: false,
      itemsList: [],
      baseFieldsRaw: [
        { key: "index", label: "", class: "grp-pat" },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing grp-pat",
        },
        { key: "doctorFullName", label: "แพทย์", class: "narrow-spacing grp-pat" },
        { key: "dn", label: "DN.", class: "narrow-spacing grp-pat" },
        {
          key: "patientFullName",
          label: "ชื่อคนไข้",
          class: "narrow-spacing grp-pat",
        },
        {
          key: "itemName",
          label: "รายการรักษา",
          class: "narrow-spacing grp-pat",
        },
        { key: "treatType", label: "ประเภท", class: "narrow-spacing grp-pat" },

        { key: "price", label: "ค่ารักษา", class: "text-right grp-rev" },
        { key: "finalDiscount", label: "ส่วนลด", class: "text-right grp-rev" },
        { key: "amount", label: "สุทธิ", class: "text-right grp-rev" },
        { key: "paid", label: "ชำระ", class: "text-right grp-rev" },
        {
          key: "overdue",
          label: "ค้างชำระ",
          class: "text-right text-danger grp-rev",
        },
      ],
      visibleRows: [],
      itemsType: [],
      selectedTx: [],
      selectedDrug: [],
      favTxValue: [],

      // pagination
      currentPage: 1,
      perPage: 100,

      isFullscreen: false,
      tableTopPos: null,
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getTreatBy: "moduleAppointment/getTreatBy",
      getItemBy: "moduleAppointment/getItemBy",
      getReportDateRange: "moduleReport/getReportDateRange",
      getGroupTxListNoFav: "moduleAppointment/getGroupTxListNoFav", //ย้าย grouping เข้าไปใน appointment module
      getGroupedItemListNoFav: "moduleAppointment/getGroupedItemListNoFav", //ย้าย grouping เข้าไปใน appointment module
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
    }),
    showFinancial() {
      return this.viewMode === "financial";
    },
    showPayment() {
      return this.viewMode === "payment";
    },
    hasAnyFee() {
      return this.itemsList.some(
        r => parseFloat(r.processingFeeBaht) > 0
      );
    },
    fields() {
      const base = this.baseFieldsRaw;
      if (this.viewMode === "payment") {
        return [
          ...base,
          { key: "paymentChips", label: "วิธีชำระ", class: "grp-pay" },
        ];
      }

      // financial mode
      const feeFields = [];
      if (this.hasAnyFee) {
        feeFields.push({
          key: "processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right grp-fee",
        });
      }
      feeFields.push(
        { key: "paidDf", label: "DF", class: "text-right grp-fee" },
        { key: "paidCf", label: "CF", class: "text-right grp-fee" }
      );
      return [...base, ...feeFields];
    },
    feeColspan() {
      if (this.viewMode !== "financial") return 0;
      return this.hasAnyFee ? 3 : 2;
    },
    distinctPaymentMethods() {
      const map = new Map();
      for (const item of this.itemsList) {
        for (const a of item.paymentAllocations || []) {
          if (!map.has(a.id)) map.set(a.id, { id: a.id, name: a.name });
        }
      }
      return [...map.values()].sort((a, b) => a.id - b.id);
    },
    totalsByMethod() {
      return this.distinctPaymentMethods
        .map(m => ({
          id: m.id,
          name: m.name,
          amount: this.filteredList.reduce((acc, item) => {
            const hit = (item.paymentAllocations || []).find(
              a => a.id === m.id
            );
            return acc + (hit ? parseFloat(hit.amount) : 0);
          }, 0),
        }))
        .filter(t => t.amount > 0);
    },
    filterItemId() {
      return this.selectedTx.map(i => i.id);
    },
    filterDrugId() {
      return this.selectedDrug.map(i => i.id);
    },

    filteredList() {
      let filteredData = this.itemsList;
      if (this.itemsType.length === 0) {
        filteredData = this.itemsList;
      } else {
        filteredData = this.itemsList.filter(i =>
          this.itemsType.includes(i.treatType)
        );
      }
      if (this.filterItemId.length > 0) {
        filteredData = this.itemsList.filter(i => {
          return this.filterItemId.includes(parseInt(i.txItemId));
        });
      }
      if (this.filterDrugId.length > 0) {
        filteredData = this.itemsList.filter(i => {
          return this.filterDrugId.includes(parseInt(i.drugItemId));
        });
      }
      return filteredData
        .slice(0)
        .reverse()
        .map((i, index) => {
          return { ...i, row: index + 1 };
        });
    },
    startDt() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    endDt() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
    tableHeight() {
      if (!this.isFullscreen) {
        return `calc(100vh - ${this.tableTopPos + 40}px)`;
      } else {
        return `calc(100vh - 90px)`;
      }
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchReportBranchReceiptItem: "moduleReport/fetchReportBranchReceiptItem",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    init() {},
    moment() {
      return moment();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },

    resetDateRange() {
      this.dateRange = {
        startDate: null,
        endDate: null,
      };
    },
    formatType(code) {
      switch (code) {
        case 1:
          return "รักษาทั่วไป";
        case 2:
          return "แพ็กเกจจัดฟัน";
        case 3:
          return "ยา และอุปกรณ์";
      }
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sum(arg) {
      let num =
        this.filteredList.reduce((accum, item) => {
          return accum + parseFloat(item[arg]);
        }, 0.0) || 0;
      return Math.round(num * 100) / 100;
    },
    formatNumber(value) {
      const num = Number(value) || 0;
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    generatePDf() {
      eventBus.$emit(
        "generatePDF",
        this.$refs["branchItemsIncomeTable"].$el,
        this.$refs.header,
        this.$refs.footer
      );
    },
    sendEmail() {
      //รอ api
    },
    fetchReport() {
      if (this.startDt === "" || this.endDt === "") return;

      this.setStateReportDateRange(this.dateRange);
      this.isLoading = true;
      this.fetchReportBranchReceiptItem({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        startDt: this.startDt,
        endDt: this.endDt,
      })
        .then(res => {
          this.itemsList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async excelExport(id) {
      document.querySelectorAll(".sr-only").forEach(e => e.remove());
      const prevMode = this.viewMode;
      this.viewMode = "financial"; // Excel-friendly view
      this.perPage = null;
      await this.$nextTick();

      const tbl = document.getElementById(id);
      const ws = XLSX.utils.table_to_sheet(tbl);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws);
      XLSX.writeFile(wb, "report.xlsx");

      this.perPage = 100;
      this.viewMode = prevMode;
    },

    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openReceipt(data) {
      eventBus.$emit("openReceipt", data);
    },
    customTxDrugsLabel(option) {
      if (option.nameTh == option.nameEn) {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}`;
      } else {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}${
          option.nameEn ? " / " + option.nameEn : ""
        }`;
      }
    },
    clearTxFilter(prop) {
      this.selectedTx = [];
    },
    clearDrugFilter(prop) {
      this.selectedDrug = [];
    },
    applySelectedTx({ selectedTx, selectedDrug }) {
      this.selectedTx = selectedTx.flat();
      this.selectedDrug = selectedDrug.flat();
    },
    addFav(e) {
      let data = {
        text: e,
        selectedTx: this.selectedTx,
        selectedDrug: this.selectedDrug,
      };
      this.$refs.favFilter.addItem(data);
    },
  },
  async mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
    await this.$nextTick();
    const tableElement = this.$refs.branchItemsIncomeTable?.$el ||
                         document.querySelector("#branchItemsIncomeTable");
    this.tableTopPos = tableElement
      ? tableElement.getBoundingClientRect().top
      : 200; // fallback value if element not found

    eventBus.$on("afterCancelReceipt", e => {
      this.fetchReport();
    });
  },
  updated() {},
};
</script>

<style>
.multiselect-h-38px .multiselect__tags {
  min-height: 38px;
}
.multiselect-h-31px .multiselect__tags {
  min-height: 31px;
}
.multiselect-h-36px .multiselect__tags {
  min-height: 36px;
}

/* ─── Compact text scale (page-scoped) ─── */
.items-income-report {
  font-size: 12px;
}
.items-income-report .field-lbl {
  font-size: 10px;
}
.items-income-report .chip {
  height: 28px;
  padding: 0 10px;
  font-size: 11.5px;
}
.items-income-report .chip-group {
  height: 32px;
}
.items-income-report .btn-primary-lg {
  height: 32px;
  font-size: 12.5px;
  padding: 0 14px;
}
.items-income-report .filter-card {
  padding: 10px 12px;
}
.items-income-report .filter-card .form-control,
.items-income-report .filter-card .multiselect__tags,
.items-income-report .field-input-native {
  min-height: 32px !important;
  height: 32px !important;
  font-size: 12px;
}
.items-income-report .multiselect__single,
.items-income-report .multiselect__placeholder,
.items-income-report .multiselect__tag {
  font-size: 11.5px;
}
.items-income-report .filter-card .input-group {
  align-items: stretch;
}
.items-income-report .filter-card .input-group-append .btn,
.items-income-report .filter-card .fi-fav {
  height: 32px;
  min-height: 32px;
  padding: 0 10px;
  line-height: 1;
  font-size: 12px;
}
.items-income-report .seg {
  height: 28px;
}
.items-income-report .seg-btn {
  height: 22px;
  font-size: 11.5px;
  padding: 0 10px;
}
.items-income-report .seg-lbl {
  font-size: 10.5px;
}
.items-income-report .toolbar .btn-sm {
  font-size: 11.5px;
  padding: 2px 8px;
}
.items-income-report .pagination-sm .page-link {
  font-size: 11.5px;
  padding: 2px 8px;
}

/* table */
.items-income-report .items-income-table {
  font-size: 11.5px;
}
.items-income-report .items-income-table th,
.items-income-report .items-income-table td {
  padding: 4px 6px;
  font-size: 11.5px;
  line-height: 1.35;
}
.items-income-report .items-income-table thead th {
  font-size: 11px;
}
.items-income-report .items-income-table thead .grp-band-row .grp-band {
  font-size: 9.5px;
  padding: 2px 6px;
}
.items-income-report .items-income-table caption {
  font-size: 10.5px;
}
.items-income-report .pmt-chip {
  font-size: 10.5px;
  padding: 1px 6px;
}

/* ─── Filter card ─── */
.filter-card {
  background: #fbfcfd;
  border: 1px solid #e5e9ef;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.fc-row {
  display: grid;
  gap: 12px;
  align-items: end;
}
.fc-row-primary {
  grid-template-columns: 280px 1fr auto;
}
.fc-row-secondary {
  grid-template-columns: 1fr 1fr 1fr;
}
@media (max-width: 991px) {
  .fc-row-primary,
  .fc-row-secondary {
    grid-template-columns: 1fr;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.field-lbl {
  font-size: 11px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}
.field-input-native {
  height: 36px !important;
  border-radius: 8px;
}
.filter-card .form-control,
.filter-card .multiselect__tags {
  min-height: 36px;
  border-radius: 8px;
}
.filter-card .input-group .btn,
.filter-card .input-group .input-group-append .btn {
  border-radius: 0 8px 8px 0;
}
.filter-card .fi-fav {
  background: #fff;
  border: 1px solid #d2d8e0;
  color: #b97309;
}
.filter-card .fi-fav:hover {
  background: #fff8e1;
  color: #b97309;
}
.btn-primary-lg {
  height: 36px;
  align-self: end;
  padding: 0 18px;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

/* ─── Type chips ─── */
.chip-group {
  display: flex;
  gap: 6px;
  align-items: center;
  height: 36px;
  flex-wrap: wrap;
}
.chip {
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #d2d8e0;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 12.5px;
  color: #4a5568;
  user-select: none;
  margin: 0;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
}
.chip input {
  display: none;
}
.chip:hover {
  border-color: #b9c3cf;
}
.chip.chip-active {
  background: #eaf1fe;
  border-color: #2a6df4;
  color: #1a4ec0;
  font-weight: 600;
}

/* ─── Toolbar / segmented switch ─── */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.seg {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f7f8fa;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
  padding: 3px;
  height: 32px;
}
.seg-lbl {
  font-size: 11.5px;
  color: #8a96a3;
  padding: 0 8px 0 6px;
}
.seg-btn {
  background: transparent;
  border: none;
  padding: 0 12px;
  height: 24px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12.5px;
  color: #4a5568;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.seg-btn:focus {
  outline: none;
}
.seg-btn.active {
  background: #fff;
  color: #1f2933;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 0 0 1px #e5e9ef;
}

/* ─── Grouped header bands (b-table) ─── */
.items-income-table thead .grp-pat {
  background: #fbfcfd !important;
}
.items-income-table thead .grp-rev {
  background: #f6f9fc !important;
}
.items-income-table thead .grp-fee {
  background: #fbf6ee !important;
}
.items-income-table thead .grp-pay {
  background: #f3faf6 !important;
}
.items-income-table tbody td.grp-pat {
  background: #fdfeff;
}
.items-income-table tbody td.grp-rev {
  background: #f9fbfe;
}
.items-income-table tbody td.grp-fee {
  background: #fdfaf3;
}
.items-income-table tbody td.grp-pay {
  background: #f9fdfb;
  min-width: 240px;
}
.items-income-table tbody tr:hover td {
  background: #f4f7fb !important;
}

/* group band row */
.items-income-table thead .grp-band-row .grp-band {
  text-align: center;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4a5568;
  padding: 4px 8px;
  border-bottom: 1px solid #e5e9ef;
}
</style>
